import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage } from 'react-intl';
import { reduxForm } from 'redux-form';
import styled from 'styled-components';
import { Button, Input, MaxLengthInput } from 'ui';
import BusinessIndicatorWizard from './business-indicator-wizard';
import * as styles from './create-order.css';
import OrderTypeWizard from './order-type-wizard';

const StyledDiv = styled.div`
  line-height: 80%;
  margin-bottom: 40px;
`;

const StyledInput = styled(Input)`
  margin-top: 6px;
  ${p => p.error && 'border-color: red !important;'}
`;

const AlternativeText = styled.span`
  color: ${p => p.theme.colors.theme}
  cursor: pointer;
  text-decoration: underline;
`;

const StyledTitle = styled.h3`
  text-transform: uppercase;
`;

class NewOrderForm extends Component {
  static propTypes = {
    data: PropTypes.object,
    handleSubmit: PropTypes.func.isRequired,
    onSelectionChange: PropTypes.func.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    invalid: PropTypes.bool.isRequired,
    dirty: PropTypes.bool,
    change: PropTypes.func,
    onIndicatorSelectionChange: PropTypes.func,
    toggleIndicatorWizard: PropTypes.func,
    useIndicatorWizard: PropTypes.bool,
    reset: PropTypes.func,
    setIndicatorDefaults: PropTypes.func,
    setDefaults: PropTypes.func,
    directSelectionOnly: PropTypes.bool,
  };

  componentDidMount() {
    const { toggleIndicatorWizard, directSelectionOnly } = this.props;
    toggleIndicatorWizard(
      directSelectionOnly ||
      localStorage.getItem('useIndicatorWizard') === 'true' ||
      false);
  }

  componentWillReceiveProps(nextProps) {
    // TODO: check if this is not an anti-pattern
    if (nextProps.data && nextProps.data !== this.props.data) {
      if (nextProps.dirty) {
        this.formEl.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  handleInputChange = (e) => {
    const { change, setIndicatorDefaults, useIndicatorWizard, setDefaults } = this.props;
    const formName = 'newOrderForm';
    change(e.target.name, e.target.value);
    // Logic for clearing some dropdowns when a specific dropdown changes
    if (e.target.name === 'splitInvoice') {
      change(formName, 'description_custom', '');
      change(formName, 'businessSubType', null);
      if (useIndicatorWizard) {
        setIndicatorDefaults();
      } else {
        setDefaults();
      }
    }
  };

  handleWizardChange = () => {
    const {
      toggleIndicatorWizard,
      useIndicatorWizard,
      setDefaults,
      setIndicatorDefaults,
    } = this.props;

    this.props.reset();
    localStorage.setItem('useIndicatorWizard', !useIndicatorWizard);
    toggleIndicatorWizard(!useIndicatorWizard);
    if (!useIndicatorWizard) {
      setIndicatorDefaults();
    } else {
      setDefaults();
    }
  };

  renderInputMaxLen = ({ input, description, max, prefix }) => <MaxLengthInput
    description={description}
    onChange={this.handleInputChange}
    prefix={prefix}
    name={input.name}
    max={max}
    value={input.value}
  />;

  renderInput = ({ defaultValue = null, input, description, disabled }) => {
    // set defaultvalue as value
    if (defaultValue) {
      this.handleInputChange({ target: { value: defaultValue, name: input.name } });
    }
    return (
      <>
        <label>{`${description} *`}</label>
        <StyledInput
          name={input.name}
          onChange={this.handleInputChange}
          value={input.value || defaultValue}
          disabled={disabled}
        />
      </>
    );
  };

  renderWizardSwitch = () => {
    const { directSelectionOnly, useIndicatorWizard } = this.props;
    if (directSelectionOnly) {
      return null;
    }
    return !useIndicatorWizard ?
      <AlternativeText
        data-test-id='use-direct-selection-link'
        onClick={() => this.handleWizardChange()}
      >
        <FormattedMessage id='orderWizard.titles.DIRECT_ALT' />
      </AlternativeText>
      :
      <AlternativeText
        data-test-id='use-bi-wizard-link'
        onClick={() => this.handleWizardChange()}
      >
        <FormattedMessage id='orderWizard.titles.WIZARD_ALT' />
      </AlternativeText>;
  };

  render() {
    const {
      data,
      handleSubmit,
      onSelectionChange,
      isSubmitting,
      invalid,
      onIndicatorSelectionChange,
      useIndicatorWizard,
      directSelectionOnly,
    } = this.props;
    return (
      <form
        ref={(el) => {
          this.formEl = el;
        }}
        onSubmit={handleSubmit}
      >
        <StyledDiv>
          <StyledTitle className={styles.sectionHeader}>
            <FormattedMessage id='orderWizard.CREATE_ORDER' />
            {' | '}
            {useIndicatorWizard || directSelectionOnly ?
              <FormattedMessage id='orderWizard.titles.TITLE_DIRECT' />
              :
              <FormattedMessage id='orderWizard.titles.TITLE_WIZARD' />
            }
          </StyledTitle>
          <div>
            {useIndicatorWizard || directSelectionOnly ?
              <FormattedMessage id='orderWizard.titles.DIRECT' />
              :
              <FormattedMessage id='orderWizard.titles.WIZARD' />
            }{' '}
            {this.renderWizardSwitch()}
          </div>
        </StyledDiv>
        {useIndicatorWizard || directSelectionOnly ?
          <BusinessIndicatorWizard
            data={data}
            onSelectionChange={onIndicatorSelectionChange}
            renderInputMaxLen={this.renderInputMaxLen}
            renderInput={this.renderInput}
            handleRadioChange={this.handleInputChange}
          />
          :
          <OrderTypeWizard
            data={data}
            onSelectionChange={onSelectionChange}
            renderInputMaxLen={this.renderInputMaxLen}
            renderInput={this.renderInput}
            handleRadioChange={this.handleInputChange}
          />
        }
        <div className={styles.createOrderBtnGrp}>
          <Button
            data-test-id='create-new-order-button'
            htmlType='submit'
            type='secondary'
            loading={isSubmitting}
            disabled={invalid}
          >
            <FormattedMessage id='orderWizard.CREATE_ORDER' />
          </Button>
        </div>
      </form>
    );
  }
}

export default reduxForm({
  form: 'newOrderForm',
})(NewOrderForm);
